"use client";

import {
  Box,
  Container,
  Title,
  Text,
  Button,
  Group,
  SimpleGrid,
  Paper,
  Stack,
  List,
  ThemeIcon,
  Badge,
  Table,
} from "@mantine/core";
import { IconArrowRight, IconCheck, IconX, IconSparkles, IconPhoto, IconPalette } from "@tabler/icons-react";
import { Link } from "@/i18n/navigation";
import { useTranslations } from "next-intl";

export default function BestColorAnalysisSoftwareContent() {
  const t = useTranslations("bestColorAnalysisSoftware");

  return (
    <Box mt="3.5rem" pos="relative">
      {/* Hero */}
      <Container size="xl" py="xl">
        <Stack gap="lg" maw={800} mx="auto">
          <Badge size="lg" color="violet" variant="light" w="fit-content">
            {t("badge")}
          </Badge>
          <Title order={1} fz={{ base: "2rem", md: "2.5rem" }} fw="bold" lh={1.2}>
            {t("title")}
          </Title>
          <Text fz="lg" c="dimmed">
            {t("subtitle")}
          </Text>
        </Stack>
      </Container>

      {/* Quick Answer */}
      <Box bg="violet.0" py="xl">
        <Container size="xl">
          <Paper p="xl" radius="md" withBorder bg="white">
            <Group gap="md" mb="md">
              <ThemeIcon size={50} radius="xl" color="violet">
                <IconSparkles size={26} />
              </ThemeIcon>
              <Box>
                <Text fw={600} fz="lg">
                  {t("shortAnswerTitle")}
                </Text>
                <Text c="dimmed" size="sm">
                  {t("shortAnswerSubtitle")}
                </Text>
              </Box>
            </Group>
            <Text mb="md">
              {t("shortAnswerText")}
            </Text>
            <Button color="violet" rightSection={<IconArrowRight size={16} />} component={Link} href="/ai-color-analysis">
              {t("tryButton")}
            </Button>
          </Paper>
        </Container>
      </Box>

      {/* Types of Software */}
      <Container size="xl" py="xl">
        <Title order={2} mb="xl">
          {t("typesTitle")}
        </Title>
        <SimpleGrid cols={{ base: 1, md: 3 }} spacing="lg">
          <Paper p="lg" radius="md" withBorder>
            <ThemeIcon size={44} radius="md" color="blue" mb="md">
              <IconPhoto size={24} />
            </ThemeIcon>
            <Text fw={600} mb="xs">
              {t("aiPhotoTitle")}
            </Text>
            <Text size="sm" c="dimmed" mb="md">
              {t("aiPhotoDescription")}
            </Text>
            <Badge color="green" variant="light">
              {t("aiPhotoBadge")}
            </Badge>
          </Paper>

          <Paper p="lg" radius="md" withBorder>
            <ThemeIcon size={44} radius="md" color="teal" mb="md">
              <IconPalette size={24} />
            </ThemeIcon>
            <Text fw={600} mb="xs">
              {t("virtualDrapingTitle")}
            </Text>
            <Text size="sm" c="dimmed" mb="md">
              {t("virtualDrapingDescription")}
            </Text>
            <Badge color="blue" variant="light">
              {t("virtualDrapingBadge")}
            </Badge>
          </Paper>

          <Paper p="lg" radius="md" withBorder>
            <ThemeIcon size={44} radius="md" color="orange" mb="md">
              <IconCheck size={24} />
            </ThemeIcon>
            <Text fw={600} mb="xs">
              {t("quizBasedTitle")}
            </Text>
            <Text size="sm" c="dimmed" mb="md">
              {t("quizBasedDescription")}
            </Text>
            <Badge color="gray" variant="light">
              {t("quizBasedBadge")}
            </Badge>
          </Paper>
        </SimpleGrid>
      </Container>

      {/* Comparison Table */}
      <Box bg="gray.0" py="xl">
        <Container size="xl">
          <Title order={2} mb="xl" ta="center">
            {t("comparisonTitle")}
          </Title>
          <Paper p="md" radius="md" withBorder bg="white" style={{ overflowX: "auto" }}>
            <Table striped highlightOnHover>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>{t("tableHeaderSoftware")}</Table.Th>
                  <Table.Th>{t("tableHeaderType")}</Table.Th>
                  <Table.Th>{t("tableHeaderPrice")}</Table.Th>
                  <Table.Th>{t("tableHeaderFeatures")}</Table.Th>
                  <Table.Th>{t("tableHeaderBestFor")}</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                <Table.Tr style={{ backgroundColor: "var(--mantine-color-violet-0)" }}>
                  <Table.Td fw={600}>{t("colorAnalysisTech")}</Table.Td>
                  <Table.Td>{t("colorAnalysisTechType")}</Table.Td>
                  <Table.Td>{t("colorAnalysisTechPrice")}</Table.Td>
                  <Table.Td>{t("colorAnalysisTechFeatures")}</Table.Td>
                  <Table.Td>{t("colorAnalysisTechBestFor")}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td fw={600}>{t("dressika")}</Table.Td>
                  <Table.Td>{t("dressikaType")}</Table.Td>
                  <Table.Td>{t("dressikaPrice")}</Table.Td>
                  <Table.Td>{t("dressikaFeatures")}</Table.Td>
                  <Table.Td>{t("dressikaBestFor")}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td fw={600}>{t("colorwise")}</Table.Td>
                  <Table.Td>{t("colorwiseType")}</Table.Td>
                  <Table.Td>{t("colorwisePrice")}</Table.Td>
                  <Table.Td>{t("colorwiseFeatures")}</Table.Td>
                  <Table.Td>{t("colorwiseBestFor")}</Table.Td>
                </Table.Tr>
                <Table.Tr>
                  <Table.Td fw={600}>{t("freeQuizTools")}</Table.Td>
                  <Table.Td>{t("freeQuizToolsType")}</Table.Td>
                  <Table.Td>{t("freeQuizToolsPrice")}</Table.Td>
                  <Table.Td>{t("freeQuizToolsFeatures")}</Table.Td>
                  <Table.Td>{t("freeQuizToolsBestFor")}</Table.Td>
                </Table.Tr>
              </Table.Tbody>
            </Table>
          </Paper>
        </Container>
      </Box>

      {/* Why ColorAnalysisTech */}
      <Container size="xl" py="xl">
        <SimpleGrid cols={{ base: 1, md: 2 }} spacing="xl">
          <Box>
            <Badge color="violet" variant="light" mb="md">
              {t("whyColorAnalysisTechBadge")}
            </Badge>
            <Title order={2} mb="md">
              {t("whyColorAnalysisTechTitle")}
            </Title>
            <Text c="dimmed" mb="lg">
              {t("whyColorAnalysisTechIntro")}
            </Text>
            <List spacing="sm" mb="lg">
              <List.Item
                icon={
                  <ThemeIcon color="green" size={20} radius="xl">
                    <IconCheck size={12} />
                  </ThemeIcon>
                }
              >
                <strong>{t("whyColorAnalysisTechPoint1")}</strong>
              </List.Item>
              <List.Item
                icon={
                  <ThemeIcon color="green" size={20} radius="xl">
                    <IconCheck size={12} />
                  </ThemeIcon>
                }
              >
                <strong>{t("whyColorAnalysisTechPoint2")}</strong>
              </List.Item>
              <List.Item
                icon={
                  <ThemeIcon color="green" size={20} radius="xl">
                    <IconCheck size={12} />
                  </ThemeIcon>
                }
              >
                <strong>{t("whyColorAnalysisTechPoint3")}</strong>
              </List.Item>
              <List.Item
                icon={
                  <ThemeIcon color="green" size={20} radius="xl">
                    <IconCheck size={12} />
                  </ThemeIcon>
                }
              >
                <strong>{t("whyColorAnalysisTechPoint4")}</strong>
              </List.Item>
              <List.Item
                icon={
                  <ThemeIcon color="green" size={20} radius="xl">
                    <IconCheck size={12} />
                  </ThemeIcon>
                }
              >
                <strong>{t("whyColorAnalysisTechPoint5")}</strong>
              </List.Item>
            </List>
            <Group>
              <Button color="violet" rightSection={<IconArrowRight size={16} />} component={Link} href="/ai-color-analysis">
                {t("tryNowButton")}
              </Button>
              <Button variant="outline" color="violet" component={Link} href="/color-analysis-quiz">
                {t("freeQuizButton")}
              </Button>
            </Group>
          </Box>
          <Paper p="xl" radius="md" withBorder bg="violet.0">
            <Stack gap="md">
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  {t("statsAccuracy")}
                </Text>
                <Text fw={600}>{t("statsAccuracyValue")}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  {t("statsTime")}
                </Text>
                <Text fw={600}>{t("statsTimeValue")}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  {t("statsColors")}
                </Text>
                <Text fw={600}>{t("statsColorsValue")}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  {t("statsDraping")}
                </Text>
                <Text fw={600}>{t("statsDrapingValue")}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  {t("statsPrice")}
                </Text>
                <Text fw={600}>{t("statsPriceValue")}</Text>
              </Group>
              <Group justify="space-between">
                <Text size="sm" c="dimmed">
                  {t("statsGuarantee")}
                </Text>
                <Text fw={600}>{t("statsGuaranteeValue")}</Text>
              </Group>
            </Stack>
          </Paper>
        </SimpleGrid>
      </Container>

      {/* What to Look For */}
      <Box bg="gray.0" py="xl">
        <Container size="xl">
          <Title order={2} mb="xl" ta="center">
            {t("whatToLookForTitle")}
          </Title>
          <SimpleGrid cols={{ base: 1, md: 2 }} spacing="lg">
            <Paper p="lg" radius="md" withBorder bg="white">
              <Title order={3} fz="lg" mb="md" c="green.7">
                {t("greenFlagsTitle")}
              </Title>
              <List spacing="xs" size="sm">
                <List.Item
                  icon={
                    <ThemeIcon color="green" size={18} radius="xl">
                      <IconCheck size={10} />
                    </ThemeIcon>
                  }
                >
                  {t("greenFlag1")}
                </List.Item>
                <List.Item
                  icon={
                    <ThemeIcon color="green" size={18} radius="xl">
                      <IconCheck size={10} />
                    </ThemeIcon>
                  }
                >
                  {t("greenFlag2")}
                </List.Item>
                <List.Item
                  icon={
                    <ThemeIcon color="green" size={18} radius="xl">
                      <IconCheck size={10} />
                    </ThemeIcon>
                  }
                >
                  {t("greenFlag3")}
                </List.Item>
                <List.Item
                  icon={
                    <ThemeIcon color="green" size={18} radius="xl">
                      <IconCheck size={10} />
                    </ThemeIcon>
                  }
                >
                  {t("greenFlag4")}
                </List.Item>
                <List.Item
                  icon={
                    <ThemeIcon color="green" size={18} radius="xl">
                      <IconCheck size={10} />
                    </ThemeIcon>
                  }
                >
                  {t("greenFlag5")}
                </List.Item>
              </List>
            </Paper>
            <Paper p="lg" radius="md" withBorder bg="white">
              <Title order={3} fz="lg" mb="md" c="red.7">
                {t("redFlagsTitle")}
              </Title>
              <List spacing="xs" size="sm">
                <List.Item
                  icon={
                    <ThemeIcon color="red" size={18} radius="xl">
                      <IconX size={10} />
                    </ThemeIcon>
                  }
                >
                  {t("redFlag1")}
                </List.Item>
                <List.Item
                  icon={
                    <ThemeIcon color="red" size={18} radius="xl">
                      <IconX size={10} />
                    </ThemeIcon>
                  }
                >
                  {t("redFlag2")}
                </List.Item>
                <List.Item
                  icon={
                    <ThemeIcon color="red" size={18} radius="xl">
                      <IconX size={10} />
                    </ThemeIcon>
                  }
                >
                  {t("redFlag3")}
                </List.Item>
                <List.Item
                  icon={
                    <ThemeIcon color="red" size={18} radius="xl">
                      <IconX size={10} />
                    </ThemeIcon>
                  }
                >
                  {t("redFlag4")}
                </List.Item>
                <List.Item
                  icon={
                    <ThemeIcon color="red" size={18} radius="xl">
                      <IconX size={10} />
                    </ThemeIcon>
                  }
                >
                  {t("redFlag5")}
                </List.Item>
              </List>
            </Paper>
          </SimpleGrid>
        </Container>
      </Box>

      {/* CTA */}
      <Container size="md" py="xl">
        <Paper p="xl" radius="md" withBorder ta="center" bg="violet.0">
          <Title order={2} mb="md">
            {t("ctaTitle")}
          </Title>
          <Text c="dimmed" mb="lg" maw={500} mx="auto">
            {t("ctaDescription")}
          </Text>
          <Group justify="center">
            <Button
              size="lg"
              color="violet"
              rightSection={<IconArrowRight size={18} />}
              component={Link}
              href="/ai-color-analysis"
            >
              {t("startButton")}
            </Button>
          </Group>
          <Text size="xs" c="dimmed" mt="md">
            {t("ctaFooter")}
          </Text>
        </Paper>
      </Container>
    </Box>
  );
}
